import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import JsonEditor from './JsonEditor';

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-modal-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000
};

const modalContentStyle = {
    backgroundColor: 'var(--bg-modal)',
    padding: '24px',
    borderRadius: '12px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    color: 'var(--text-primary)',
    boxShadow: '0 8px 32px var(--shadow-strong)',
    border: '1px solid var(--border-color)'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px'
};

const titleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    margin: 0,
    color: 'var(--text-primary)'
};

const closeButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    fontSize: '1.5rem',
    padding: '0 8px',
    transition: 'color 0.2s'
};

const bodyStyle = {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '20px'
};

const footerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border-color)'
};

const selectStyle = {
    width: '100%',
    padding: '10px',
    marginBottom: '16px',
    backgroundColor: 'var(--bg-input)',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '14px'
};

const buttonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.2s'
};

const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--accent-color)',
    color: 'var(--text-inverse)'
};

const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--bg-tertiary)',
    color: 'var(--text-primary)'
};

const resultStyle = {
    backgroundColor: 'var(--bg-secondary)',
    padding: '16px',
    borderRadius: '8px',
    marginTop: '16px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    border: '1px solid var(--border-color)'
};

const AI_CREDENTIAL_TYPES = new Set(['openai', 'gemini', 'google_generativeai']);

export default function CopilotModal({ isOpen, onClose, mode, workflowId, runId }) {
    const [credentials, setCredentials] = useState([]);
    const [selectedCredentialId, setSelectedCredentialId] = useState('');
    const [approvalId, setApprovalId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchCredentials();
            setResult(null);
            setError(null);
            setApprovalId('');
        }
    }, [isOpen]);

    const fetchCredentials = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.get(`${API_BASE_URL}/credentials/`, { headers });
            const allCreds = Array.isArray(res.data) ? res.data : [];
            const aiCreds = allCreds.filter(c => AI_CREDENTIAL_TYPES.has(c.service_type));
            setCredentials(aiCreds);
            if (aiCreds.length > 0) {
                setSelectedCredentialId(aiCreds[0].id);
            } else {
                setSelectedCredentialId('');
            }
        } catch (err) {
            console.error("Failed to fetch credentials", err);
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 401) {
                setError("Your session expired. Please log in again.");
                return;
            }
            setError(detail || "Failed to load credentials. Please ensure you have an OpenAI or Gemini credential configured.");
        }
    };

    const handleAction = async () => {
        if (!selectedCredentialId) {
            setError("Please select an AI credential.");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            let endpoint = '';
            let payload = { credential_id: selectedCredentialId };

            if (mode === 'fix') {
                endpoint = '/copilot/fix';
                payload.run_id = runId;
                if (approvalId.trim()) {
                    payload.approval_id = approvalId.trim();
                }
            } else if (mode === 'explain') {
                endpoint = '/copilot/explain';
                payload.workflow_id = workflowId;
            } else if (mode === 'inputs') {
                endpoint = '/copilot/generate-inputs';
                payload.workflow_id = workflowId;
            }

            const res = await axios.post(`${API_BASE_URL}${endpoint}`, payload);
            setResult(res.data);
            if (res.data?.status === 'pending_approval' && res.data?.approval_id) {
                setApprovalId(res.data.approval_id);
            }
        } catch (err) {
            console.error("Copilot action failed", err);
            setError(err.response?.data?.detail || "An error occurred while communicating with the AI service.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const getTitle = () => {
        switch (mode) {
            case 'fix': return 'Auto-Fix with AI';
            case 'explain': return 'Explain Workflow';
            case 'inputs': return 'Generate Test Inputs';
            default: return 'Copilot';
        }
    };

    const renderResult = () => {
        if (!result) return null;

        if (result.status === 'pending_approval') {
            return (
                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--warning-bg, rgba(251, 191, 36, 0.12))', border: '1px solid var(--warning-border, rgba(251, 191, 36, 0.35))' }}>
                    <div style={{ color: 'var(--warning-color)', marginBottom: '8px' }}>{result.message || 'Pending approval'}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Approval ID: {result.approval_id}</div>
                    <div style={{ marginTop: '8px', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                        Ask a workspace admin to approve this request, then run again.
                    </div>
                </div>
            );
        }

        if (mode === 'inputs') {
            return (
                <div style={{ marginTop: '16px' }}>
                    <div style={{ marginBottom: '8px', color: 'var(--text-tertiary)' }}>Generated Inputs (JSON):</div>
                    <JsonEditor
                        value={JSON.stringify(result.inputs, null, 2)}
                        onChange={() => {}} // Read-only for now
                        height="300px"
                    />
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                        Provider: {result.provider}
                    </div>
                </div>
            );
        }

        const content = result.suggestion || result.explanation || JSON.stringify(result, null, 2);

        return (
            <div style={resultStyle}>
                <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-color)', paddingTop: '4px' }}>
                    Provider: {result.provider}
                </div>
            </div>
        );
    };

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h2 style={titleStyle}>{getTitle()}</h2>
                    <button style={closeButtonStyle} onClick={onClose}>&times;</button>
                </div>

                <div style={bodyStyle}>
                    {credentials.length === 0 && !loading && (
                        <div style={{ color: 'var(--warning-color)', marginBottom: '16px', padding: '12px', backgroundColor: 'var(--warning-bg, rgba(251, 191, 36, 0.1))', borderRadius: '6px' }}>
                            No AI credentials found. Please add an OpenAI or Gemini credential in the Credential Manager.
                        </div>
                    )}

                    {credentials.length > 0 && (
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Select AI Model:</label>
                            <select 
                                style={selectStyle}
                                value={selectedCredentialId}
                                onChange={e => setSelectedCredentialId(e.target.value)}
                            >
                                {credentials.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name} ({c.service_type})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {mode === 'fix' && (
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)' }}>Approval ID (optional):</label>
                            <input
                                style={selectStyle}
                                value={approvalId}
                                onChange={e => setApprovalId(e.target.value)}
                                placeholder="Paste approval ID after admin approval"
                            />
                        </div>
                    )}

                    {error && (
                        <div style={{ color: '#ef4444', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '6px' }}>
                            {error}
                        </div>
                    )}

                    {!result && !loading && credentials.length > 0 && (
                        <div style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                            Ready to analyze. Click "Run AI Analysis" to proceed.
                        </div>
                    )}

                    {loading && (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--accent-color)' }}>
                            Thinking... 🤖
                        </div>
                    )}

                    {renderResult()}
                </div>

                <div style={footerStyle}>
                    <button style={secondaryButtonStyle} onClick={onClose}>Close</button>
                    <button 
                        style={{...primaryButtonStyle, opacity: credentials.length === 0 || loading ? 0.5 : 1}} 
                        onClick={handleAction}
                        disabled={credentials.length === 0 || loading}
                    >
                        Run AI Analysis
                    </button>
                </div>
            </div>
        </div>
    );
}
